import {Injectable} from "@angular/core";
import {FactorBaseService} from "./factor-base-service";
import {ParameterRule} from "@ispm-client/modules/query-builder/models/query-builder-config";


@Injectable()
export class DisorderService extends FactorBaseService {
  static readonly TUMOR = "tumor";
  static readonly SPACE_TUMOR = " tumor";
  static readonly TUMORGRADE = "tumorgrade";
  static readonly GRADE_SPACE = "Grade ";
  static readonly TUMORSIZEUNITS = "tumorsizeunits";
  static readonly TUMORSIZEQUALITATIVE = "tumorsizequalitative";
  static readonly TUMORSIZEVALUE = "tumorsizevalue";
  static readonly TUMORRESECTABILITY = "tumorresectability";
  static readonly CANCERTYPE = "cancertype";
  static readonly HISTOLOGY = "histology";
  static readonly PRIMARYTUMORSITE = "primarytumorsite";
  static readonly SYSTEM = "system";
  static readonly CLLBINET = "cllbinet";
  static readonly CLLRAI = "cllrai";
  static readonly GROUPSTAGE = "groupstage";
  static readonly SCLCSTAGE = "sclcstage";
  static readonly MISCELLANEOUSSTAGE = "miscellaneousstage";
  static readonly TSTAGE = "tstage";
  static readonly MSTAGE = "mstage";
  static readonly NSTAGE = "nstage";
  static readonly STAGE = "Stage ";
  static readonly SCLC = " SCLC";
  static readonly RAI = " RAI";
  static readonly BINET = " BINET";
  static readonly CONDITION = "condition";
  static readonly SYMPTOMATICITY = "symptomaticity";
  static readonly DISEASEVOLUME = "diseasevolume";
  static readonly SEVERITY = "severity";
  static readonly DOMINANCE = "dominance";
  //static readonly LYMPHOMADISEASETYPE = "lymphomadiseasetype";
  static readonly MELANOMADISEASECHARACTERISTIC = "melanomadiseasecharacteristic";
  static readonly LEUKEMIADISEASECHARACTERISTIC = "leukemiadiseasecharacteristic";
  static readonly TRANSFORMATIONSTATUS = "transformationstatus";
  static readonly TRANSFORMATIONTYPE = "transformationtype";
  static readonly DISEASEBULK = "diseasebulk";
  static readonly BONELESIONTYPE = "bonelesiontype";
  static readonly BONEMARROWINVOLVEMENT = "bonemarrowinvolvement";
  static readonly CANCERGROWTHRATE = "cancergrowthrate";
  static readonly NUMBEROFBONELESIONS = "numberofbonelesions";
  static readonly LESION = " lesion";
  static readonly LESIONS = " lesions";
  static readonly OTHER = "Other";
  static readonly YES = "Yes";
  static readonly OTHER_BONE_LESION_TYPE = "Other bone lesion type";
  static readonly BONE_MARROW_INVOLVEMENT = "bone marrow involvement";
  static readonly METASTASISTYPE = "metastasistype";
  static readonly METASTATICSITE = "metastaticsite";
  static readonly NUMBEROFMETASTASES = "numberofmetastases";
  static readonly PRESENCEOFMETASTASES = "presenceofmetastases";
  static readonly NO = "No";
  static readonly IN = " in ";
  static readonly IS = "is";
  static readonly ES = "es";
  static readonly EXTRANODALEXTENSION = "extranodalextension";
  static readonly NODENUMBER = "nodenumber";
  static readonly SITE = "site";
  static readonly EXTRA_NODAL_EXTENSION = "extra nodal extension";
  static readonly NODE_INVOLVEMENT = " node involvement";
  static readonly NODES = " nodes";
  static readonly NODE = " node";
  static readonly DISEASESTATEATPRIORTHERAPY = "diseasestateatpriortherapy";
  static readonly DISEASEEXTENTATPRIORTHERAPY = "diseaseextentatpriortherapy";
  static readonly CURRENTDISEASESTATE = "currentdiseasestate";
  static readonly CURRENTDISEASEEXTENT = "currentdiseaseextent";
  static readonly CMLPHASE = "cmlphase";
  static readonly AT_PRIOR_THERAPY = "at prior therapy:";
  static readonly DEAUVILLESCORINGSYSTEM = "deauvillescoringsystem";
  static readonly MDSIPSS = "mdsipss";
  static readonly IPSS = "IPSS: ";
  static readonly DEAUVILLE = "Deauville: ";


  public getDisplayName(parameterRule: ParameterRule) : string {
    this.parameterRulesMap = {};
    let parameterRules = parameterRule.rules;
    for (let rule of parameterRules) {
      this.parameterRulesMap[rule.field.toLowerCase()] = rule;
    }

    //get disorder type
    let parameter: string = parameterRule.parameter.toLowerCase();
    let disorderName: string = "";
    switch (parameter.toLowerCase()) {
      case "diseasecharacteristics": {
        disorderName = this.disorderCharacteristics();
        break;
      }
      case "diseasescoringsystem": {
        disorderName = this.disorderScoringSystem();
        break;
      }
      case "diseasediagnosis": {
        disorderName = this.disorderDiagnosis();
        break;
      }
      case "diseasenodeinvolvement": {
        disorderName = this.disorderNodeInvolvement();
        break;
      }
      case "diseasestate": {
        disorderName = this.disorderState();
        break;
      }
      case "diseasemetastasis": {
        disorderName = this.disorderMetastasis();
        break;
      }
      case "diseasestage": {
        disorderName = this.disorderStage();
        break;
      }
      case "diseasetumor": {
        disorderName = this.disorderTumor();
        break;
      }
    }
    return disorderName;
  }

  private disorderCharacteristics(): string {
    /*
      Disease - Characteristics:

      {Disease Volume}
      {Severity (MDS)}
      {Dominance (MDS)}
      {Symptomaticity}
      {Cancer Growth Rate}

      {Condition}
      //{Lymphoma Disease Type}
      {Melanoma Disease Characteristic}
      {Leukemia Disease Characteristic}
      {Transformation}

      {Disease Bulk}

      [{# of Bone Lesions} lesions]
      {Bone Lesion Type!=Other|Other bone lesion type}
      [{Bone Marrow Involvement==Yes?bone marrow involvement}]

      Meaning:

      •	Push them to the output in this order
      •	Add ‘lesions’ if there is a ‘# of Bone Lesions’
      •	Substitute ‘Other’ with ‘Other bone lesion type’ for ‘Bone Lesion Type’
      •	If ‘Bone Marrow Involvement’ is ‘yes’, push ‘bone marrow involvement’
     */

    var results: string[] = new Array();
    let rule = null;

    this.diseaseVolume(results);
    this.severity(results);
    this.dominance(results);
    this.symptomaticity(results);
    this.cancerGrowthRate(results);
    this.condition(results);
    //this.lymphomaDiseaseType(results);
    this.melanomaDiesaseCharacteristics(results);
    this.leukemiaDiesaseCharacteristics(results);
    this.transformationType(results);
    this.transformationStatus(results);
    this.diseaseBulk(results);
    this.numberOfBoneLesions(results);
    this.boneLesionType(results);
    this.boneMarrowInvolvemnet(results);

    let delimitedString = results.join(FactorBaseService.SPACE);
    return delimitedString;
  }

  private disorderScoringSystem(): string {
    /*
      Disease - Scoring System
      [Deauville: {Deauville Scoring System}] [IPSS: {MDS - IPSS}] [Age-adjusted IPI: {Age-Adjusted IPI}]
     */
    var results: string[] = new Array();
    let rule = null;

    this.deauvilleScoringSystem(results);
    this.mdsIpss(results);

    let delimitedString = results.join(FactorBaseService.SPACE);
    return delimitedString;
  }

  private disorderDiagnosis(): string {
    /*
      Disease - Diagnosis:
      {Cancer Type} {Histology} {Site}
     */
    var results: string[] = new Array();
    let rule = null;

    this.cancerType(results);
    this.histology(results);
    this.primaryTumorSite(results);

    let delimitedString = results.join(FactorBaseService.SPACE);
    return delimitedString;
  }

  private disorderNodeInvolvement(): string {
    /*
      Disease - Node Involvement:
      [{Site} node involvement] {ExtraNodal Extension==yes?extra nodal extension} [{Node Number} of nodes]
     */
    var results: string[] = new Array();
    let rule = null;

    this.nodeSite(results);
    this.extraNodalExtension(results);
    this.nodeNumber(results);

    let delimitedString = results.join(FactorBaseService.SPACE);
    return delimitedString;
  }

  private disorderState(): string {
    /*
      Disease - State
      {Current Disease Extent} {Current Disease State} [at prior therapy: {Current Disease Extent} {Current Disease State}] {CML Phase}
     */

    var results: string[] = new Array();
    let rule = null;

    this.currentDiseaseExtent(results);
    this.currentDiseaseState(results);
    if (this.keyExists(DisorderService.DISEASEEXTENTATPRIORTHERAPY) || this.keyExists(DisorderService.DISEASESTATEATPRIORTHERAPY)) {
      results.push(DisorderService.AT_PRIOR_THERAPY);
      this.diseaseExtentAtPriorTherapy(results);
      this.diseaseStateAtPriorTherapy(results);
    }
    this.cmlPhase(results);


    let delimitedString = results.join(FactorBaseService.SPACE);
    return delimitedString;
  }

  private disorderMetastasis(): string {
    /*
      Disease - Metastasis:
      {Presence of Metastases==no} {Number of Metastases} {Metastasis Type} metastas{Number of Metastases==1?i|e}s [in {Metastatic Site}]
     */

    var results: string[] = new Array();
    let rule = null;

    this.presenceOfMetastases(results);
    if (results.length === 0) {
      let count: number = this.numberOfMetastases(results);
      this.metastasisType(results);
      this.metastaticSite(count, results);
    }

    let delimitedString = results.join(FactorBaseService.SPACE);
    return delimitedString;
  }

  private disorderStage(): string {
    /*
      {System} {Type} [Stage {Group Stage}] {T Stage} {M Stage} {N Stage}
      Stage {CLL - RAI} (RAI)
      Stage {CLL - BINET} (BINET)
      {SCLC Stage} (SCLC)
      {Miscellaneous Stage}
     */

    var results: string[] = new Array();
    let rule = null;

    if (this.keyExists(DisorderService.SYSTEM) || this.keyExists(DisorderService._TYPE) || this.keyExists(DisorderService.GROUPSTAGE) ||
        this.keyExists(DisorderService.TSTAGE) || this.keyExists(DisorderService.MSTAGE) || this.keyExists(DisorderService.NSTAGE)) {
      //{System} {Type} [Stage {Group Stage}] {T Stage} {M Stage} {N Stage}
      this.system(results);
      this.type(results);
      this.groupStage(results);
      this.tStage(results);
      this.mStage(results);
      this.nStage(results);
    }
    if (this.keyExists(DisorderService.CLLBINET)) {
      //Stage {CLL - BINET} (BINET)
      this.cllBinet(results);
    }
    if (this.keyExists(DisorderService.CLLRAI)) {
      //Stage {CLL - RAI} (RAI)
      this.cllRai(results);
    }
    if (this.keyExists(DisorderService.SCLCSTAGE)) {
      //{SCLC Stage} (SCLC)
      this.sclcStage(results);
    }
    if (this.keyExists(DisorderService.MISCELLANEOUSSTAGE)) {
      //{Miscellaneous Stage}
      this.miscellaneousStage(results);
    }

    let delimitedString = results.join(DisorderService.SPACE);
    return delimitedString;
  }


  private disorderTumor(): string {
    /*
      [{Tumor} tumor] [Grade {Grade}] {Size} {Value} {Units} {Resectability}
     */
    var results: string[] = new Array();
    let rule = null;

    this.tumor(results);
    this.tumorGrade(results);
    this.tumorSizeQualitative(results);
    this.tumorSizeValue(results);
    this.tumorSizeUnits(results);
    this.tumorRespectability(results);

    let delimitedString = results.join(FactorBaseService.SPACE);
    return delimitedString;
  }

  private tumor(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.TUMOR];
    if (rule) {
      let displayValue: string = rule.value.display + DisorderService.SPACE_TUMOR;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private tumorGrade(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.TUMORGRADE];
    if (rule) {
      let displayValue: string = rule.value.display;
      displayValue = DisorderService.GRADE_SPACE + displayValue;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private tumorSizeQualitative(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.TUMORSIZEQUALITATIVE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private tumorSizeUnits(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.TUMORSIZEUNITS];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private tumorSizeValue(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.TUMORSIZEVALUE];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private tumorRespectability(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.TUMORRESECTABILITY];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private cancerType(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.CANCERTYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private histology(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.HISTOLOGY];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private primaryTumorSite(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.PRIMARYTUMORSITE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private system(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.SYSTEM];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private type(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService._TYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private miscellaneousStage(results: string[]) {
    //{Miscellaneous Stage}
    let rule = this.parameterRulesMap[DisorderService.MISCELLANEOUSSTAGE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private sclcStage(results: string[]) {
    //{SCLC Stage} (SCLC)
    let rule = this.parameterRulesMap[DisorderService.SCLCSTAGE];
    if (rule) {
      let displayValue: string = rule.value.display + DisorderService.SCLC;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private groupStage(results: string[]) {
    //[Stage {Group Stage}]
    let rule = this.parameterRulesMap[DisorderService.GROUPSTAGE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results, DisorderService.STAGE);
    }
  }

  private cllRai(results: string[]) {
    //Stage {CLL - RAI} (RAI)
    let rule = this.parameterRulesMap[DisorderService.CLLRAI];
    if (rule) {
      let displayValue: string = rule.value.display + DisorderService.RAI;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private cllBinet(results: string[]) {
    //Stage {CLL - BINET} (BINET)
    let rule = this.parameterRulesMap[DisorderService.CLLBINET];
    if (rule) {
      let displayValue: string = rule.value.display + DisorderService.BINET;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private tStage(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.TSTAGE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results, DisorderService.STAGE);
    }
  }

  private mStage(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.MSTAGE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results, DisorderService.STAGE);
    }
  }

  private nStage(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.NSTAGE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results, DisorderService.STAGE);
    }
  }

  private diseaseVolume(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.DISEASEVOLUME];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private severity(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.SEVERITY];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private dominance(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.DOMINANCE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private symptomaticity(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.SYMPTOMATICITY];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private cancerGrowthRate(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.CANCERGROWTHRATE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private condition(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.CONDITION];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  // private lymphomaDiseaseType(results: string[]) {
  //   let rule = this.parameterRulesMap[DisorderService.LYMPHOMADISEASETYPE];
  //   if (rule) {
  //     let displayValue: string = rule.value.display;
  //     this.pushWithOperator(rule, displayValue, results);
  //   }
  // }

  private melanomaDiesaseCharacteristics(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.MELANOMADISEASECHARACTERISTIC];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private leukemiaDiesaseCharacteristics(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.LEUKEMIADISEASECHARACTERISTIC];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private transformationType(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.TRANSFORMATIONTYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private transformationStatus(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.TRANSFORMATIONSTATUS];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private diseaseBulk(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.DISEASEBULK];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private numberOfBoneLesions(results: string[]) {
    /*
      [{# of Bone Lesions} lesions]
      Meaning: Add ‘lesions’ if there is a ‘# of Bone Lesions’
     */
    let rule = this.parameterRulesMap[DisorderService.NUMBEROFBONELESIONS];
    if (rule) {
      let displayValue: string = "";
      if (rule.value === 1)
        displayValue = rule.value + DisorderService.LESION;
      else
        displayValue = rule.value + DisorderService.LESIONS;

      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private boneLesionType(results: string[]) {
    /*
    {Bone Lesion Type!=Other|Other bone lesion type}
    Meaning: Substitute ‘Other’ with ‘Other bone lesion type’ for ‘Bone Lesion Type’
    */
    let rule = this.parameterRulesMap[DisorderService.BONELESIONTYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      if (displayValue === DisorderService.OTHER)
        displayValue = DisorderService.OTHER_BONE_LESION_TYPE;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private boneMarrowInvolvemnet(results: string[]) {
    /*
      [{Bone Marrow Involvement==Yes?bone marrow involvement}]
      Meaning: If ‘Bone Marrow Involvement’ is ‘yes’, push ‘bone marrow involvemen
      */
    let rule = this.parameterRulesMap[DisorderService.BONEMARROWINVOLVEMENT];
    if (rule) {
      let displayValue: string = rule.value.display;
      if (displayValue === DisorderService.YES)
        displayValue = DisorderService.BONE_MARROW_INVOLVEMENT;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private presenceOfMetastases(results: string[]) {
    //{Presence of Metastases==no}
    let rule = this.parameterRulesMap[DisorderService.PRESENCEOFMETASTASES];
    if (rule) {
      let displayValue: string = rule.value.display;
      if (displayValue !== DisorderService.YES)
        this.pushWithOperator(rule, displayValue, results);
    }
  }

  private numberOfMetastases(results: string[]): number {
    let rule = this.parameterRulesMap[DisorderService.NUMBEROFMETASTASES];
    if (rule) {
      let displayValue: string = rule.value;
      this.pushWithOperator(rule, displayValue, results);
      return parseInt(displayValue);
    }
    return 0;
  }

  private metastasisType(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.METASTASISTYPE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private metastaticSite(count: number, results: string[]) {
    //metastas{Number of Metastases==1?i|e}s [in {Metastatic Site}]
    let rule = this.parameterRulesMap[DisorderService.METASTATICSITE];
    if (rule) {
      let prefix: string = "metastas";
      if (count === 1) {
        prefix += DisorderService.IS;   //metastasis
      }
      else {
        prefix += DisorderService.ES;   //metastases
      }
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results, prefix + DisorderService.IN);
    }
  }

  /*
  Disease - Node Involvement:
  [{Site} node involvement] {ExtraNodal Extension==yes?extra nodal extension} [{Node Number} of nodes]
 */

  private nodeSite(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.SITE];
    if (rule) {
      let displayValue: string = rule.value.display + DisorderService.NODE_INVOLVEMENT;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private extraNodalExtension(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.EXTRANODALEXTENSION];
    if (rule) {
      let displayValue: string = rule.value.display;
      if (displayValue === DisorderService.YES) {
        displayValue = DisorderService.EXTRA_NODAL_EXTENSION;
        this.pushWithOperator(rule, displayValue, results);
      }
    }
  }

  private nodeNumber(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.NODENUMBER];
    if (rule) {
      let displayValue: any = rule.value;
      if (displayValue === 1)
        displayValue += DisorderService.NODE;
      else
        displayValue += DisorderService.NODES;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  /*
  Disease - State
  {Current Disease Extent} {Current Disease State} [at prior therapy: {Current Disease Extent} {Current Disease State}] {CML Phase}
*/

  private currentDiseaseExtent(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.CURRENTDISEASEEXTENT];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private currentDiseaseState(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.CURRENTDISEASESTATE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private diseaseExtentAtPriorTherapy(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.DISEASEEXTENTATPRIORTHERAPY];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private diseaseStateAtPriorTherapy(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.DISEASESTATEATPRIORTHERAPY];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  private cmlPhase(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.CMLPHASE];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results);
    }
  }

  /*
   Disease - Scoring System
   [Deauville: {Deauville Scoring System}] [IPSS: {MDS - IPSS}] [Age-adjusted IPI: {Age-Adjusted IPI}]
  this.deauvilleScoringSystem(results);
  this.mdsIpss(results);
    */

  private deauvilleScoringSystem(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.DEAUVILLESCORINGSYSTEM];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results, DisorderService.DEAUVILLE);
    }
  }

  private mdsIpss(results: string[]) {
    let rule = this.parameterRulesMap[DisorderService.MDSIPSS];
    if (rule) {
      let displayValue: string = rule.value.display;
      this.pushWithOperator(rule, displayValue, results, DisorderService.IPSS);
    }
  }

}
